import { supabase } from '../lib/supabase';

// ============================================================
// LISTINGS SERVICE
// ============================================================
export const listingsService = {
  async getAll({ mode = 'buy', region = 'australia', location = '', query = '', minPrice = '', maxPrice = '', beds = '', propertyType = '', limit = 20, offset = 0 } = {}) {
    try {
      let q = supabase?.from('listings')?.select(`
          *,
          properties:property_id (*),
          media (*)
        `)?.eq('status', 'live')?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      if (mode && mode !== 'all') q = q?.eq('listing_type', mode);
      if (minPrice) q = q?.gte('price_or_rent', parseFloat(minPrice));
      if (maxPrice) q = q?.lte('price_or_rent', parseFloat(maxPrice));

      const { data, error } = await q;
      if (error) throw error;

      let results = data || [];

      // Client-side filters for nested fields
      if (region && region !== 'all') {
        results = results?.filter(l => l?.properties?.region === region);
      }
      if (location) {
        const loc = location?.toLowerCase();
        results = results?.filter(l =>
          l?.properties?.suburb?.toLowerCase()?.includes(loc) ||
          l?.properties?.address?.toLowerCase()?.includes(loc) ||
          l?.properties?.state?.toLowerCase()?.includes(loc)
        );
      }
      if (query) {
        const q2 = query?.toLowerCase();
        results = results?.filter(l =>
          l?.properties?.address?.toLowerCase()?.includes(q2) ||
          l?.properties?.suburb?.toLowerCase()?.includes(q2) ||
          l?.description_short?.toLowerCase()?.includes(q2)
        );
      }
      if (beds) {
        results = results?.filter(l => (l?.properties?.beds || 0) >= parseInt(beds));
      }
      if (propertyType) {
        results = results?.filter(l => l?.properties?.property_type === propertyType);
      }

      return { data: results, error: null };
    } catch (error) {
      console.error('Error fetching listings:', error);
      return { data: [], error };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('listings')?.select(`
          *,
          properties:property_id (*),
          media (*)
        `)?.eq('id', id)?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching listing:', error);
      return { data: null, error };
    }
  },

  async getByAgent(agentId) {
    try {
      const { data, error } = await supabase?.from('listings')?.select(`
          *,
          properties:property_id (*),
          media (*)
        `)?.eq('agent_id', agentId)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching agent listings:', error);
      return { data: [], error };
    }
  },

  async create({ propertyData, listingData, agentId }) {
    try {
      // Create property first
      const { data: property, error: propError } = await supabase?.from('properties')?.insert({
          address: propertyData?.address || '',
          suburb: propertyData?.suburb || '',
          state: propertyData?.state || '',
          postcode: propertyData?.postcode || '',
          region: propertyData?.region || 'australia',
          property_type: propertyData?.propertyType || 'house',
          beds: propertyData?.beds || 0,
          baths: propertyData?.baths || 0,
          parking: propertyData?.parking || 0,
          owner_id: agentId
        })?.select()?.single();

      if (propError) throw propError;

      // Create listing
      const { data: listing, error: listError } = await supabase?.from('listings')?.insert({
          property_id: property?.id,
          agent_id: agentId,
          listing_type: listingData?.listingType || 'buy',
          status: listingData?.saveAsDraft ? 'draft' : 'live',
          price_or_rent: listingData?.price ? parseFloat(String(listingData?.price)?.replace(/[^0-9.]/g, '')) : null,
          price_display: listingData?.price || '',
          description_short: listingData?.descriptionShort || '',
          description_long: listingData?.descriptionLong || ''
        })?.select()?.single();

      if (listError) throw listError;
      return { data: { property, listing }, error: null };
    } catch (error) {
      console.error('Error creating listing:', error);
      return { data: null, error };
    }
  },

  async updateStatus(listingId, status) {
    try {
      const { data, error } = await supabase?.from('listings')?.update({ status })?.eq('id', listingId)?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating listing status:', error);
      return { data: null, error };
    }
  },

  async delete(listingId) {
    try {
      const { error } = await supabase?.from('listings')?.delete()?.eq('id', listingId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting listing:', error);
      return { error };
    }
  },

  subscribeToAgentListings(agentId, callback) {
    return supabase?.channel(`agent_listings_${agentId}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'listings',
        filter: `agent_id=eq.${agentId}`
      }, callback)?.subscribe();
  }
};

// ============================================================
// PROFESSIONALS SERVICE
// ============================================================
export const professionalsService = {
  async getAll({ category = 'all', region = 'australia', search = '', sortBy = 'rating' } = {}) {
    try {
      let q = supabase?.from('pro_profiles')?.select(`
          *,
          user:user_id (full_name, email, avatar_url),
          services:pro_services (*),
          portfolio:pro_portfolio (*),
          reviews:pro_reviews (*)
        `)?.eq('active', true)?.order('rating', { ascending: false });

      if (category && category !== 'all') q = q?.eq('category', category);

      const { data, error } = await q;
      if (error) throw error;

      let results = data || [];

      if (search) {
        const s = search?.toLowerCase();
        results = results?.filter(p =>
          p?.user?.full_name?.toLowerCase()?.includes(s) ||
          p?.company?.toLowerCase()?.includes(s) ||
          p?.specializations?.some(sp => sp?.toLowerCase()?.includes(s))
        );
      }

      if (sortBy === 'reviews') results = [...results]?.sort((a, b) => (b?.review_count || 0) - (a?.review_count || 0));
      else if (sortBy === 'jobs') results = [...results]?.sort((a, b) => (b?.completed_jobs || 0) - (a?.completed_jobs || 0));

      return { data: results, error: null };
    } catch (error) {
      console.error('Error fetching professionals:', error);
      return { data: [], error };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('pro_profiles')?.select(`
          *,
          user:user_id (full_name, email, avatar_url),
          services:pro_services (*),
          portfolio:pro_portfolio (*),
          reviews:pro_reviews (*)
        `)?.eq('id', id)?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching professional:', error);
      return { data: null, error };
    }
  },

  async create(userId, profileData) {
    try {
      const { data, error } = await supabase?.from('pro_profiles')?.insert({
          user_id: userId,
          category: profileData?.category || 'agent',
          company: profileData?.company || '',
          bio: profileData?.bio || '',
          location: profileData?.location || '',
          region: profileData?.region || 'australia',
          service_areas: profileData?.serviceAreas || [],
          specializations: profileData?.specializations ? profileData?.specializations?.split(',')?.map(s => s?.trim()) : [],
          certifications: profileData?.certifications || [],
          starting_price: profileData?.startingRate || '',
          availability: profileData?.availability || '',
          phone: profileData?.phone || '',
          website: profileData?.website || '',
          instagram: profileData?.instagram || '',
          rera_number: profileData?.reraNumber || '',
          brn_number: profileData?.brnNumber || '',
          orn_number: profileData?.ornNumber || '',
          trade_license: profileData?.tradeLicense || ''
        })?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating pro profile:', error);
      return { data: null, error };
    }
  }
};

// ============================================================
// LEADS SERVICE
// ============================================================
export const leadsService = {
  async create(leadData) {
    try {
      const { data, error } = await supabase?.from('leads')?.insert({
          listing_id: leadData?.listingId || null,
          user_id: leadData?.userId || null,
          lead_type: leadData?.type || 'enquiry',
          name: leadData?.name || '',
          email: leadData?.email || '',
          phone: leadData?.phone || '',
          message: leadData?.message || '',
          stage: 'new'
        })?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating lead:', error);
      return { data: null, error };
    }
  },

  async getByAgent(agentId) {
    try {
      const { data, error } = await supabase?.from('leads')?.select(`
          *,
          listing:listing_id (
            *,
            properties:property_id (address, suburb)
          )
        `)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching leads:', error);
      return { data: [], error };
    }
  },

  async updateStage(leadId, stage) {
    try {
      const { data, error } = await supabase?.from('leads')?.update({ stage })?.eq('id', leadId)?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================================
// BOOKINGS SERVICE
// ============================================================
export const bookingsService = {
  async create(bookingData) {
    try {
      const { data, error } = await supabase?.from('bookings')?.insert({
          user_id: bookingData?.userId || null,
          pro_id: bookingData?.proId,
          service_name: bookingData?.serviceName || '',
          booking_date: bookingData?.date || null,
          booking_time: bookingData?.time || '',
          notes: bookingData?.notes || '',
          name: bookingData?.name || '',
          email: bookingData?.email || '',
          phone: bookingData?.phone || '',
          status: 'pending'
        })?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating booking:', error);
      return { data: null, error };
    }
  },

  async getByUser(userId) {
    try {
      const { data, error } = await supabase?.from('bookings')?.select(`
          *,
          professional:pro_id (company, category, location, user:user_id (full_name))
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};

// ============================================================
// ANALYSES SERVICE
// ============================================================
export const analysesService = {
  async getByUser(userId) {
    try {
      const { data, error } = await supabase?.from('analyses')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching analyses:', error);
      return { data: [], error };
    }
  },

  async create(userId, analysisData) {
    try {
      const { data, error } = await supabase?.from('analyses')?.insert({
          user_id: userId,
          address: analysisData?.address || '',
          suburb: analysisData?.suburb || '',
          region: analysisData?.region || 'australia',
          verdict: analysisData?.verdict || 'Fair',
          confidence_score: analysisData?.confidence_score ?? analysisData?.confidenceScore ?? 80,
          portal: analysisData?.portal || 'Manual Entry',
          analysis_data: analysisData?.data || {}
        })?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error saving analysis:', error);
      return { data: null, error };
    }
  },

  async getAll() {
    try {
      const { data, error } = await supabase?.from('analyses')?.select(`
          *,
          user:user_id (full_name, email)
        `)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  subscribeToUserAnalyses(userId, callback) {
    return supabase?.channel(`user_analyses_${userId}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'analyses',
        filter: `user_id=eq.${userId}`
      }, callback)?.subscribe();
  }
};

// ============================================================
// FAVORITES SERVICE
// ============================================================
export const favoritesService = {
  async getByUser(userId) {
    try {
      const { data, error } = await supabase?.from('favorites')?.select(`
          *,
          listing:listing_id (
            *,
            properties:property_id (*),
            media (*)
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async toggle(userId, listingId) {
    try {
      // Check if already favorited
      const { data: existing } = await supabase?.from('favorites')?.select('id')?.eq('user_id', userId)?.eq('listing_id', listingId)?.maybeSingle();

      if (existing) {
        const { error } = await supabase?.from('favorites')?.delete()?.eq('id', existing?.id);
        if (error) throw error;
        return { data: { favorited: false }, error: null };
      } else {
        const { data, error } = await supabase?.from('favorites')?.insert({ user_id: userId, listing_id: listingId })?.select()?.single();
        if (error) throw error;
        return { data: { favorited: true, favorite: data }, error: null };
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return { data: null, error };
    }
  },

  async isFavorited(userId, listingId) {
    try {
      const { data } = await supabase?.from('favorites')?.select('id')?.eq('user_id', userId)?.eq('listing_id', listingId)?.maybeSingle();
      return !!data;
    } catch {
      return false;
    }
  }
};

// ============================================================
// ACTIVITY SERVICE
// ============================================================
export const activityService = {
  async getByUser(userId) {
    try {
      const { data, error } = await supabase?.from('activity_history')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(100);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async log(userId, activityData) {
    try {
      const { error } = await supabase?.from('activity_history')?.insert({
          user_id: userId,
          activity_type: activityData?.type || 'view',
          title: activityData?.title || '',
          detail: activityData?.detail || '',
          tags: activityData?.tags || [],
          metadata: activityData?.metadata || {}
        });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error logging activity:', error);
      return { error };
    }
  }
};

// ============================================================
// ADMIN SERVICE
// ============================================================
export const adminService = {
  async getStats() {
    try {
      const [usersRes, listingsRes, analysesRes, leadsRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true }),
        supabase?.from('listings')?.select('*', { count: 'exact', head: true }),
        supabase?.from('analyses')?.select('*', { count: 'exact', head: true }),
        supabase?.from('leads')?.select('*', { count: 'exact', head: true })
      ]);
      return {
        users: usersRes?.count || 0,
        listings: listingsRes?.count || 0,
        analyses: analysesRes?.count || 0,
        leads: leadsRes?.count || 0
      };
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      return { users: 0, listings: 0, analyses: 0, leads: 0 };
    }
  },

  async getAllUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getAllListings() {
    try {
      const { data, error } = await supabase?.from('listings')?.select(`
          *,
          properties:property_id (address, suburb, state),
          agent:agent_id (full_name, email)
        `)?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};

// ============================================================
// STORAGE SERVICE
// ============================================================
export const storageService = {
  async uploadPropertyImage(file, listingId) {
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${listingId}/${Date.now()}.${ext}`;
      const { data, error } = await supabase?.storage?.from('property-images')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data: { publicUrl } } = supabase?.storage?.from('property-images')?.getPublicUrl(path);
      return { data: { path, publicUrl }, error: null };
    } catch (error) {
      console.error('Error uploading property image:', error);
      return { data: null, error };
    }
  },

  async uploadPortfolioImage(file, proId) {
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${proId}/${Date.now()}.${ext}`;
      const { data, error } = await supabase?.storage?.from('pro-portfolios')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data: { publicUrl } } = supabase?.storage?.from('pro-portfolios')?.getPublicUrl(path);
      return { data: { path, publicUrl }, error: null };
    } catch (error) {
      console.error('Error uploading portfolio image:', error);
      return { data: null, error };
    }
  },

  async uploadDocument(file, userId) {
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${userId}/${Date.now()}-${file?.name}`;
      const { data, error } = await supabase?.storage?.from('documents')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data: signedData, error: signedError } = await supabase?.storage?.from('documents')?.createSignedUrl(path, 3600);
      if (signedError) throw signedError;
      return { data: { path, signedUrl: signedData?.signedUrl }, error: null };
    } catch (error) {
      console.error('Error uploading document:', error);
      return { data: null, error };
    }
  },

  getPropertyImageUrl(path) {
    const { data: { publicUrl } } = supabase?.storage?.from('property-images')?.getPublicUrl(path);
    return publicUrl;
  },

  getPortfolioImageUrl(path) {
    const { data: { publicUrl } } = supabase?.storage?.from('pro-portfolios')?.getPublicUrl(path);
    return publicUrl;
  }
};
